/**
 * Z101 SuperMaster API Client
 * Connects frontend to backend at api.z101.io
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.z101.io';

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 - Token expired
      if (response.status === 401) {
        this.setToken(null);
        window.dispatchEvent(new CustomEvent('auth:logout'));
        throw new Error('Session expired. Please login again.');
      }

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || 60;
        throw new Error(`Rate limited. Try again in ${retryAfter} seconds.`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // GET request
  get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  // POST request
  post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Create singleton instance
const api = new ApiClient();

export default api;

// ============================================
// AUTH ENDPOINTS
// ============================================

export const authApi = {
  login: (email, password) => api.post('/api/v1/auth/login', { email, password }),
  register: (data) => api.post('/api/v1/auth/register', data),
  logout: () => api.post('/api/v1/auth/logout'),
  refresh: () => api.post('/api/v1/auth/refresh'),
  me: () => api.get('/api/v1/users/me'),
};

// ============================================
// USERS ENDPOINTS
// ============================================

export const usersApi = {
  list: (params) => api.get('/api/v1/admin/users', params),
  get: (id) => api.get(`/api/v1/users/${id}`),
  update: (id, data) => api.put(`/api/v1/users/${id}`, data),
  delete: (id) => api.delete(`/api/v1/users/${id}`),
  sessions: (userId) => api.get(`/api/v1/users/${userId}/sessions`),
  revokeSession: (userId, sessionId) => api.delete(`/api/v1/users/${userId}/sessions/${sessionId}`),
};

// ============================================
// COMPANIES/TENANTS ENDPOINTS
// ============================================

export const tenantsApi = {
  list: (params) => api.get('/api/branding/list', params),
  get: (id) => api.get(`/api/v1/companies/${id}`),
  create: (data) => api.post('/api/v1/companies', data),
  update: (id, data) => api.put(`/api/v1/companies/${id}`, data),
  delete: (id) => api.delete(`/api/v1/companies/${id}`),
  suspend: (id) => api.post(`/api/v1/admin/companies/${id}/suspend`),
  reactivate: (id) => api.post(`/api/v1/admin/companies/${id}/reactivate`),
  usage: (id) => api.get(`/api/v1/admin/companies/${id}/usage`),
};

// ============================================
// CAMPAIGNS ENDPOINTS
// ============================================

export const campaignsApi = {
  list: (params) => api.get('/api/v1/campaigns', params),
  get: (id) => api.get(`/api/v1/campaigns/${id}`),
  create: (data) => api.post('/api/v1/campaigns', data),
  update: (id, data) => api.put(`/api/v1/campaigns/${id}`, data),
  delete: (id) => api.delete(`/api/v1/campaigns/${id}`),
  start: (id) => api.post(`/api/v1/campaigns/${id}/start`),
  pause: (id) => api.post(`/api/v1/campaigns/${id}/pause`),
  stop: (id) => api.post(`/api/v1/campaigns/${id}/stop`),
  stats: (id) => api.get(`/api/v1/campaigns/${id}/stats`),
  clone: (id) => api.post(`/api/v1/campaigns/${id}/clone`),
};

// ============================================
// LEADS/CRM ENDPOINTS
// ============================================

export const leadsApi = {
  list: (params) => api.get('/api/v1/leads', params),
  get: (id) => api.get(`/api/v1/leads/${id}`),
  create: (data) => api.post('/api/v1/leads', data),
  update: (id, data) => api.put(`/api/v1/leads/${id}`, data),
  delete: (id) => api.delete(`/api/v1/leads/${id}`),
};

export const contactsApi = {
  list: (params) => api.get('/api/v1/contacts', params),
  get: (id) => api.get(`/api/v1/contacts/${id}`),
  create: (data) => api.post('/api/v1/contacts', data),
  update: (id, data) => api.put(`/api/v1/contacts/${id}`, data),
  delete: (id) => api.delete(`/api/v1/contacts/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.request('/api/v1/contacts/import', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set content-type for FormData
    });
  },
};

export const dealsApi = {
  list: (params) => api.get('/api/v1/deals', params),
  get: (id) => api.get(`/api/v1/deals/${id}`),
  create: (data) => api.post('/api/v1/deals', data),
  update: (id, data) => api.put(`/api/v1/deals/${id}`, data),
  delete: (id) => api.delete(`/api/v1/deals/${id}`),
};

// ============================================
// EMAIL ENDPOINTS
// ============================================

export const emailApi = {
  accounts: {
    list: () => api.get('/api/v1/email-accounts'),
    create: (data) => api.post('/api/v1/email-accounts', data),
    delete: (id) => api.delete(`/api/v1/email-accounts/${id}`),
  },
  warmup: {
    status: () => api.get('/api/v1/warm-up/status'),
    start: (accountId) => api.post('/api/v1/warm-up/start', { accountId }),
    stop: (accountId) => api.post('/api/v1/warm-up/stop', { accountId }),
  },
  contacts: {
    list: (params) => api.get('/api/v1/email/contacts', params),
    import: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.request('/api/v1/email/contacts/import', {
        method: 'POST',
        body: formData,
        headers: {},
      });
    },
  },
};

// ============================================
// SMS ENDPOINTS
// ============================================

export const smsApi = {
  campaigns: {
    list: () => api.get('/api/v1/sms/campaigns'),
    create: (data) => api.post('/api/v1/sms/campaigns', data),
  },
  send: (data) => api.post('/api/v1/sms/send', data),
  analytics: () => api.get('/api/v1/sms/analytics'),
  contacts: {
    list: (params) => api.get('/api/v1/sms/contacts', params),
    import: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.request('/api/v1/sms/contacts/import', {
        method: 'POST',
        body: formData,
        headers: {},
      });
    },
  },
};

// ============================================
// ANALYTICS ENDPOINTS
// ============================================

export const analyticsApi = {
  dashboard: () => api.get('/api/v1/analytics/dashboard'),
  campaigns: (params) => api.get('/api/v1/analytics/campaigns', params),
  realtime: () => api.get('/api/v1/integration/analytics/realtime'),
  performance: (params) => api.get('/api/v1/integration/analytics/performance', params),
  growth: (params) => api.get('/api/v1/integration/analytics/growth', params),
  export: (params) => api.get('/api/v1/analytics/export', params),
};

// ============================================
// ADMIN ENDPOINTS
// ============================================

export const adminApi = {
  dashboard: () => api.get('/api/v1/admin/dashboard'),
  health: () => api.get('/api/v1/admin/health'),
  metrics: () => api.get('/api/v1/admin/metrics'),
  plans: {
    list: () => api.get('/api/v1/admin/plans'),
    create: (data) => api.post('/api/v1/admin/plans', data),
  },
  features: {
    list: () => api.get('/api/v1/admin/features'),
    enable: (companyId, feature) => api.post(`/api/v1/admin/companies/${companyId}/features/${feature}/enable`),
    disable: (companyId, feature) => api.post(`/api/v1/admin/companies/${companyId}/features/${feature}/disable`),
  },
  auditLogs: (params) => api.get('/api/v1/admin/audit-logs', params),
};

// ============================================
// BILLING ENDPOINTS
// ============================================

export const billingApi = {
  credits: () => api.get('/api/v1/billing/credits'),
  purchase: (data) => api.post('/api/v1/billing/purchase', data),
  usage: (params) => api.get('/api/v1/billing/usage', params),
  invoices: () => api.get('/api/v1/billing/invoices'),
};

// ============================================
// AI ENDPOINTS
// ============================================

export const aiApi = {
  generate: (data) => api.post('/api/v1/ai/generate', data),
  analyze: (data) => api.post('/api/v1/ai/analyze', data),
  models: () => api.get('/api/v1/ai/models'),
};

// ============================================
// VIDEO ENDPOINTS
// ============================================

export const videoApi = {
  list: (params) => api.get('/api/v1/videos', params),
  get: (id) => api.get(`/api/v1/videos/${id}`),
  process: (data) => api.post('/api/v1/videos/process', data),
  status: (id) => api.get(`/api/v1/videos/${id}/status`),
  delete: (id) => api.delete(`/api/v1/videos/${id}`),
};

// ============================================
// SOCIAL MEDIA ENDPOINTS
// ============================================

export const socialApi = {
  accounts: {
    list: () => api.get('/api/v1/social/accounts'),
    connect: (platform) => api.post('/api/v1/social/accounts/connect', { platform }),
    disconnect: (id) => api.delete(`/api/v1/social/accounts/${id}`),
  },
  posts: {
    list: (params) => api.get('/api/v1/social/posts', params),
    create: (data) => api.post('/api/v1/social/posts', data),
    schedule: (data) => api.post('/api/v1/social/posts/schedule', data),
  },
  analytics: () => api.get('/api/v1/social/analytics'),
};

// ============================================
// FRAUD/SECURITY ENDPOINTS
// ============================================

export const fraudApi = {
  events: (params) => api.get('/api/v1/fraud/events', params),
  score: (userId) => api.get(`/api/v1/fraud/score/${userId}`),
  block: (userId) => api.post(`/api/v1/fraud/block/${userId}`),
  unblock: (userId) => api.post(`/api/v1/fraud/unblock/${userId}`),
  rules: {
    list: () => api.get('/api/v1/fraud/rules'),
    update: (data) => api.put('/api/v1/fraud/rules', data),
  },
};

// ============================================
// ACTIVITY LOG ENDPOINTS
// ============================================

export const activityApi = {
  list: (params) => api.get('/api/v1/admin/audit-logs', params),
  export: (params) => api.get('/api/v1/admin/audit-logs/export', params),
};

// ============================================
// API KEYS ENDPOINTS
// ============================================

export const apiKeysApi = {
  list: () => api.get('/api/v1/api-keys'),
  create: (data) => api.post('/api/v1/api-keys', data),
  revoke: (id) => api.delete(`/api/v1/api-keys/${id}`),
};

// ============================================
// WEBHOOKS ENDPOINTS
// ============================================

export const webhooksApi = {
  list: () => api.get('/api/v1/webhooks'),
  create: (data) => api.post('/api/v1/webhooks', data),
  update: (id, data) => api.put(`/api/v1/webhooks/${id}`, data),
  delete: (id) => api.delete(`/api/v1/webhooks/${id}`),
  test: (id) => api.post(`/api/v1/webhooks/${id}/test`),
};

// ============================================
// COMPANY-SPECIFIC ENDPOINTS
// ============================================

export const companyApi = {
  list: () => api.get('/api/v1/companies'),
  get: (id) => api.get(`/api/v1/companies/${id}`),
  getMetrics: (id) => api.get(`/api/v1/companies/${id}/metrics`),
  getDashboard: (id) => api.get(`/api/v1/companies/${id}/dashboard`),
  update: (id, data) => api.put(`/api/v1/companies/${id}`, data),
};

// ============================================
// STRIPE ENDPOINTS (moved to lazyApi - see bottom of file)
// ============================================

// ============================================
// AUTH1 MANAGEMENT ENDPOINTS
// ============================================

// Auth1 API base URL - can be configured separately from main API
const AUTH1_API_URL = import.meta.env.VITE_AUTH1_API_URL || 'https://api.auth1.ai';

class Auth1ApiClient {
  constructor() {
    this.baseUrl = AUTH1_API_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = api.getToken();

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Auth1 API request failed');
      }
      return data;
    } catch (error) {
      console.error(`Auth1 API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, data = {}) {
    return this.request(endpoint, { method: 'POST', body: JSON.stringify(data) });
  }
}

const auth1Client = new Auth1ApiClient();

export const auth1Api = {
  // Status check
  status: () => auth1Client.get('/health'),

  // Dashboard summary
  getDashboard: (tenantId) => auth1Client.get(`/api/manage/dashboard/${tenantId}`),

  // Tenants (cross-tenant management)
  listTenants: (params) => auth1Client.get('/api/manage/tenants', params),
  getTenant: (idOrSlug) => auth1Client.get(`/api/manage/tenants/${idOrSlug}`),

  // Users per tenant
  listUsers: (tenantId, params) => auth1Client.get(`/api/manage/tenants/${tenantId}/users`, params),
  getUser: (tenantId, userId) => auth1Client.get(`/api/manage/tenants/${tenantId}/users/${userId}`),
  getUserStats: (tenantId) => auth1Client.get(`/api/manage/tenants/${tenantId}/users/stats`),
  blockUser: (tenantId, userId, reason) => auth1Client.post(`/api/manage/tenants/${tenantId}/users/${userId}/block`, { reason }),
  unblockUser: (tenantId, userId) => auth1Client.post(`/api/manage/tenants/${tenantId}/users/${userId}/unblock`),

  // Sessions per tenant
  listSessions: (tenantId, params) => auth1Client.get(`/api/manage/tenants/${tenantId}/sessions`, params),
  getSessionStats: (tenantId) => auth1Client.get(`/api/manage/tenants/${tenantId}/sessions/stats`),
  revokeSession: (tenantId, sessionId) => auth1Client.post(`/api/manage/tenants/${tenantId}/sessions/${sessionId}/revoke`),
  revokeUserSessions: (tenantId, userId) => auth1Client.post(`/api/manage/tenants/${tenantId}/users/${userId}/revoke-sessions`),

  // Audit logs per tenant
  getAuditLogs: (tenantId, params) => auth1Client.get(`/api/manage/tenants/${tenantId}/audit`, params),
  getAuditStats: (tenantId, hours = 24) => auth1Client.get(`/api/manage/tenants/${tenantId}/audit/stats`, { hours }),
  getEventDistribution: (tenantId, hours = 24) => auth1Client.get(`/api/manage/tenants/${tenantId}/audit/distribution`, { hours }),

  // Security alerts
  getSecurityAlerts: (tenantId, params) => auth1Client.get(`/api/manage/tenants/${tenantId}/security/alerts`, params),
};

// ============================================
// LAZY MARKETING - EMAIL DOMAINS
// ============================================

export const domainsApi = {
  list: (companyId) => api.get(`/api/v1/email/domains`, { companyId }),
  get: (domainId) => api.get(`/api/v1/email/domains/${domainId}`),
  create: (data) => api.post('/api/v1/email/domains', data),
  delete: (domainId) => api.delete(`/api/v1/email/domains/${domainId}`),

  // Health & DNS
  checkHealth: (domainId) => api.get(`/api/v1/email/domains/${domainId}/health`),
  verifyDns: (domainId) => api.post(`/api/v1/email/domains/${domainId}/verify-dns`),

  // Warmup
  startWarmup: (domainId) => api.post(`/api/v1/email/domains/${domainId}/warmup/start`),
  stopWarmup: (domainId) => api.post(`/api/v1/email/domains/${domainId}/warmup/stop`),
  getWarmupStatus: (domainId) => api.get(`/api/v1/email/domains/${domainId}/warmup/status`),
};

// ============================================
// LAZY MARKETING - COLD EMAIL CAMPAIGNS
// ============================================

export const coldEmailApi = {
  // Campaigns
  listCampaigns: (companyId, params) => api.get('/api/v1/cold-email/campaigns', { companyId, ...params }),
  getCampaign: (campaignId) => api.get(`/api/v1/cold-email/campaigns/${campaignId}`),
  createCampaign: (data) => api.post('/api/v1/cold-email/campaigns', data),
  updateCampaign: (campaignId, data) => api.put(`/api/v1/cold-email/campaigns/${campaignId}`, data),
  deleteCampaign: (campaignId) => api.delete(`/api/v1/cold-email/campaigns/${campaignId}`),

  // Campaign controls
  startCampaign: (campaignId) => api.post(`/api/v1/cold-email/campaigns/${campaignId}/start`),
  pauseCampaign: (campaignId) => api.post(`/api/v1/cold-email/campaigns/${campaignId}/pause`),
  stopCampaign: (campaignId) => api.post(`/api/v1/cold-email/campaigns/${campaignId}/stop`),

  // Campaign stats
  getCampaignStats: (campaignId) => api.get(`/api/v1/cold-email/campaigns/${campaignId}/stats`),
  getCampaignEmails: (campaignId, params) => api.get(`/api/v1/cold-email/campaigns/${campaignId}/emails`, params),

  // Email sequences
  getSequence: (campaignId) => api.get(`/api/v1/cold-email/campaigns/${campaignId}/sequence`),
  updateSequence: (campaignId, sequence) => api.put(`/api/v1/cold-email/campaigns/${campaignId}/sequence`, { sequence }),

  // Templates
  listTemplates: (companyId) => api.get('/api/v1/cold-email/templates', { companyId }),
  getTemplate: (templateId) => api.get(`/api/v1/cold-email/templates/${templateId}`),
  createTemplate: (data) => api.post('/api/v1/cold-email/templates', data),
  updateTemplate: (templateId, data) => api.put(`/api/v1/cold-email/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/api/v1/cold-email/templates/${templateId}`),
};

// ============================================
// LAZY MARKETING - LEADS
// ============================================

export const coldLeadsApi = {
  // Lead management
  list: (companyId, params) => api.get('/api/v1/leads', { companyId, ...params }),
  get: (leadId) => api.get(`/api/v1/leads/${leadId}`),
  create: (data) => api.post('/api/v1/leads', data),
  update: (leadId, data) => api.put(`/api/v1/leads/${leadId}`, data),
  delete: (leadId) => api.delete(`/api/v1/leads/${leadId}`),
  bulkDelete: (leadIds) => api.post('/api/v1/leads/bulk-delete', { leadIds }),

  // Import
  importCsv: (file, companyId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId);
    return api.request('/api/v1/leads/import/csv', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
  getImportHistory: (companyId) => api.get('/api/v1/leads/import/history', { companyId }),

  // LinkedIn scraping
  scrapeLinkedIn: (data) => api.post('/api/v1/leads/scrape/linkedin', data),
  getScrapeStatus: (jobId) => api.get(`/api/v1/leads/scrape/status/${jobId}`),
  cancelScrape: (jobId) => api.post(`/api/v1/leads/scrape/cancel/${jobId}`),

  // Enrichment
  enrichLead: (leadId) => api.post(`/api/v1/leads/${leadId}/enrich`),
  bulkEnrich: (leadIds) => api.post('/api/v1/leads/bulk-enrich', { leadIds }),

  // Scoring
  updateScore: (leadId, score) => api.put(`/api/v1/leads/${leadId}/score`, { score }),
  recalculateScores: (companyId) => api.post('/api/v1/leads/recalculate-scores', { companyId }),
};

// ============================================
// LAZY MARKETING - AI RESPONSES
// ============================================

export const aiResponseApi = {
  // Inbox
  getInbox: (companyId, params) => api.get('/api/v1/ai-response/inbox', { companyId, ...params }),
  getMessage: (messageId) => api.get(`/api/v1/ai-response/inbox/${messageId}`),
  archiveMessage: (messageId) => api.post(`/api/v1/ai-response/inbox/${messageId}/archive`),

  // AI generation
  generateResponse: (messageId, options) => api.post(`/api/v1/ai-response/generate/${messageId}`, options),
  sendResponse: (messageId, content) => api.post(`/api/v1/ai-response/send/${messageId}`, { content }),

  // Auto-response config
  getConfig: (companyId) => api.get(`/api/v1/ai-response/config/${companyId}`),
  updateConfig: (companyId, config) => api.put(`/api/v1/ai-response/config/${companyId}`, config),

  // Response templates by category
  getTemplates: (companyId) => api.get('/api/v1/ai-response/templates', { companyId }),
  createTemplate: (data) => api.post('/api/v1/ai-response/templates', data),
  updateTemplate: (templateId, data) => api.put(`/api/v1/ai-response/templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`/api/v1/ai-response/templates/${templateId}`),

  // Analytics
  getAnalytics: (companyId, params) => api.get('/api/v1/ai-response/analytics', { companyId, ...params }),
};

// ============================================
// LAZY MARKETING API (api.lazy.z101.ai)
// Email Domain Management & LinkedIn Scraping
// ============================================

const LAZY_MARKETING_API_URL = import.meta.env.VITE_LAZY_MARKETING_API_URL || 'https://api.lazy.z101.ai';

class LazyMarketingClient {
  constructor() {
    this.baseUrl = LAZY_MARKETING_API_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = api.getToken();

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed');
    }

    return data;
  }

  get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

const lazyApi = new LazyMarketingClient();

// Email Domains (Lazy Marketing)
export const emailDomainsApi = {
  list: () => lazyApi.get('/api/v1/domains'),
  get: (id) => lazyApi.get(`/api/v1/domains/${id}`),
  create: (data) => lazyApi.post('/api/v1/domains', data),
  delete: (id) => lazyApi.delete(`/api/v1/domains/${id}`),
  verify: (id) => lazyApi.post(`/api/v1/domains/${id}/verify`),
  getHealth: (id) => lazyApi.get(`/api/v1/domains/${id}/health`),
  startWarmup: (id) => lazyApi.post(`/api/v1/domains/${id}/warmup/start`),
  pauseWarmup: (id) => lazyApi.post(`/api/v1/domains/${id}/warmup/pause`),
  getWarmup: (id) => lazyApi.get(`/api/v1/domains/${id}/warmup`),
  getNameservers: (id) => lazyApi.get(`/api/v1/domains/${id}/nameservers`),
};

// Mailboxes
export const mailboxesApi = {
  list: (params) => lazyApi.get('/api/v1/mailboxes', params),
  get: (id) => lazyApi.get(`/api/v1/mailboxes/${id}`),
  create: (data) => lazyApi.post('/api/v1/mailboxes', data),
  update: (id, data) => lazyApi.patch(`/api/v1/mailboxes/${id}`, data),
  delete: (id) => lazyApi.delete(`/api/v1/mailboxes/${id}`),
  getStats: (id) => lazyApi.get(`/api/v1/mailboxes/${id}/stats`),
};

// LinkedIn Scraper
export const linkedinApi = {
  // Scraping jobs
  startScrape: (data) => lazyApi.post('/api/v1/linkedin/scrape', data),
  scrapeSingle: (profileUrl) => lazyApi.post('/api/v1/linkedin/scrape/single', { profile_url: profileUrl }),
  getJob: (jobId) => lazyApi.get(`/api/v1/linkedin/jobs/${jobId}`),
  getJobProfiles: (jobId) => lazyApi.get(`/api/v1/linkedin/jobs/${jobId}/profiles`),
  listJobs: (params) => lazyApi.get('/api/v1/linkedin/jobs', params),

  // Contacts
  listContacts: (params) => lazyApi.get('/api/v1/linkedin/contacts', params),
  getContact: (id) => lazyApi.get(`/api/v1/linkedin/contacts/${id}`),
  deleteContact: (id) => lazyApi.delete(`/api/v1/linkedin/contacts/${id}`),
  importCSV: (contacts) => lazyApi.post('/api/v1/linkedin/import/csv', { contacts }),
};

// Campaigns (Lazy Marketing)
export const lazyCampaignsApi = {
  list: (params) => lazyApi.get('/api/v1/campaigns', params),
  get: (id) => lazyApi.get(`/api/v1/campaigns/${id}`),
  create: (data) => lazyApi.post('/api/v1/campaigns', data),
  update: (id, data) => lazyApi.patch(`/api/v1/campaigns/${id}`, data),
  delete: (id) => lazyApi.delete(`/api/v1/campaigns/${id}`),
  addContacts: (id, contactIds) => lazyApi.post(`/api/v1/campaigns/${id}/contacts`, { contact_ids: contactIds }),
  getContacts: (id, params) => lazyApi.get(`/api/v1/campaigns/${id}/contacts`, params),
  removeContact: (id, contactId) => lazyApi.delete(`/api/v1/campaigns/${id}/contacts/${contactId}`),
  start: (id) => lazyApi.post(`/api/v1/campaigns/${id}/start`),
  pause: (id) => lazyApi.post(`/api/v1/campaigns/${id}/pause`),
  getStats: (id) => lazyApi.get(`/api/v1/campaigns/${id}/stats`),
};

// Health check
export const lazyHealthApi = {
  check: () => lazyApi.get('/health'),
  detailed: () => lazyApi.get('/health/detailed'),
};

// Stripe Revenue Reporting
export const stripeApi = {
  // Get all companies summary
  getSummary: () => lazyApi.get('/api/v1/stripe/summary'),

  // Company-specific endpoints
  getMetrics: (company, params) => lazyApi.get(`/api/v1/stripe/${company}/metrics`, params),
  getCharges: (company, limit = 20) => lazyApi.get(`/api/v1/stripe/${company}/charges`, { limit }),
  getSubscriptions: (company, params) => lazyApi.get(`/api/v1/stripe/${company}/subscriptions`, params),
  getCustomers: (company, limit = 100) => lazyApi.get(`/api/v1/stripe/${company}/customers`, { limit }),
  getTrend: (company, months = 12) => lazyApi.get(`/api/v1/stripe/${company}/trend`, { months }),
};
